import React, { useEffect, useState } from 'react';
import {
  Card,
  Table,
  Tag,
  Button,
  Modal,
  Descriptions,
  Typography,
  Row,
  Col,
  Statistic,
  message,
  Input,
  Select,
  Image,
  Space,
  Tabs,
  Badge,
} from 'antd';
import {
  FileTextOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ToolOutlined,
  UserOutlined,
  EnvironmentOutlined,
  SearchOutlined,
  AppstoreOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { interventionReportsService, InterventionReport } from '../services/interventionReportsService';
import { interventionsService } from '../services/interventionsService';
import { getImageUrl } from '../services/uploadService';
import { useAuth } from '../contexts/AuthContext';

const { Title } = Typography;
const { Search } = Input;

const InterventionReportsPage: React.FC = () => {
  const { currentUser } = useAuth();
  const isAgent = currentUser?.role === 'agent';
  const [reports, setReports] = useState<InterventionReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedReport, setSelectedReport] = useState<InterventionReport | null>(null);
  const [selectedIntervention, setSelectedIntervention] = useState<any | null>(null);
  const [interventionImages, setInterventionImages] = useState<any[]>([]);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [activeTabKey, setActiveTabKey] = useState<string>('merged');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  const selectedDiag = selectedReport?.diagnostic_report || selectedIntervention?.diagnostic_report || (selectedIntervention?.diagnosticReports && selectedIntervention.diagnosticReports[0]);
  const selectedQuote = selectedDiag?.quotes?.[0] || selectedIntervention?.quote;

  const loadReports = async (page: number, limit: number) => {
    try {
      setLoading(true);
      const response = await interventionReportsService.getReports({
        page,
        limit,
        status: statusFilter || undefined,
      });

      setReports(response.data);
      setPagination({
        current: response.pagination.page,
        pageSize: response.pagination.limit,
        total: response.pagination.total,
      });
    } catch (error) {
      console.error('Erreur chargement rapports:', error);
      message.error('Erreur lors du chargement des rapports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReports(1, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const handleTableChange = (pag: any) => {
    loadReports(pag.current, pag.pageSize);
  };

  const handleViewReport = async (report: InterventionReport) => {
    setSelectedReport(report);
    setSelectedIntervention(null);
    setActiveTabKey('merged');
    setDetailModalVisible(true);
    
    try {
      const interventionId = report.id;
      if (interventionId) {
        const intervention = await interventionsService.getInterventionById(interventionId);
        setSelectedIntervention(intervention);
        
        console.log('🔍 Données intervention complètes:', intervention);
        const reportImages = intervention.report_images || [];
        
        if (reportImages.length > 0) {
          setInterventionImages(reportImages);
        } else {
          setInterventionImages([]);
        }
      }
    } catch (error) {
      console.error('❌ Erreur lors du chargement des détails:', error);
      setInterventionImages([]);
    }
  };

  const handleCloseModal = () => {
    setDetailModalVisible(false);
    setSelectedReport(null);
    setSelectedIntervention(null);
    setInterventionImages([]);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'submitted': return 'blue';
      case 'approved': return 'green';
      case 'rejected': return 'red';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'submitted': return 'Soumis';
      case 'approved': return 'Approuvé';
      case 'rejected': return 'Rejeté';
      default: return status;
    }
  };

  // Filtrer les rapports localement
  const filteredReports = reports.filter(report => {
    const matchesSearch = searchTerm === '' ||
      report.intervention_title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.technician?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.customer?.name.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch;
  });

  // Statistiques
  const stats = {
    total: pagination.total,
    soumis: reports.filter(r => r.report.status === 'submitted').length,
    approuves: reports.filter(r => r.report.status === 'approved').length,
  };

  const columns = [
    {
      title: 'Intervention',
      dataIndex: 'intervention_title',
      key: 'intervention_title',
      ellipsis: true,
      render: (text: string, record: InterventionReport) => (
        <div>
          <div style={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
            <span>{text}</span>
            {record.diagnostic_report && (
              <Tag color="purple" style={{ fontSize: '11px', margin: 0, padding: '0 6px' }}>
                🔍 Diag #{record.diagnostic_report.id}
              </Tag>
            )}
            {(record.report?.initial_completed || record.initial_completed || record.report?.problem_description) && (
              <Tag color="orange" style={{ fontSize: '11px', margin: 0, padding: '0 6px' }}>
                📋 Constat initial
              </Tag>
            )}
          </div>
          <div style={{ fontSize: '12px', color: '#888' }}>
            <EnvironmentOutlined /> {record.intervention_address || 'Non spécifiée'}
          </div>
        </div>
      ),
    },
    {
      title: 'Technicien',
      key: 'technician',
      render: (record: InterventionReport) => (
        <div>
          <UserOutlined /> {record.technician?.name || 'N/A'}
        </div>
      ),
    },
    {
      title: 'Client',
      key: 'customer',
      render: (record: InterventionReport) => (
        <div>
          {record.customer?.name || 'N/A'}
        </div>
      ),
    },
    {
      title: 'Date Soumission',
      dataIndex: ['report', 'submitted_at'],
      key: 'submitted_at',
      render: (date: string) => dayjs(date).format('DD/MM/YYYY HH:mm'),
      sorter: (a: InterventionReport, b: InterventionReport) =>
        dayjs(a.report.submitted_at).unix() - dayjs(b.report.submitted_at).unix(),
    },
    {
      title: 'Durée',
      dataIndex: ['report', 'duration'],
      key: 'duration',
      render: (duration: number) => `${duration} min`,
    },
    {
      title: 'Statut',
      dataIndex: ['report', 'status'],
      key: 'status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {getStatusLabel(status)}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: InterventionReport) => (
        <Button
          type="primary"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => handleViewReport(record)}
        >
          Voir
        </Button>
      ),
    },
  ];

  const renderDiagnosticSection = (diag: any, quoteItem?: any) => {
    if (!diag) return null;

    let equipmentsList: any[] = [];
    if (diag.equipments) {
      if (typeof diag.equipments === 'string') {
        try {
          equipmentsList = JSON.parse(diag.equipments);
        } catch (_) {
          equipmentsList = [];
        }
      } else if (Array.isArray(diag.equipments)) {
        equipmentsList = diag.equipments;
      }
    }

    if (equipmentsList.length === 0 && (diag.pression || diag.freon || diag.puissance || diag.intensite || diag.tension)) {
      equipmentsList = [{
        index: 1,
        before_pression: diag.pression,
        before_puissance: diag.puissance,
        before_intensite: diag.intensite,
        before_tension: diag.tension,
        before_freon: diag.freon,
      }];
    }

    let partsList: any[] = [];
    if (diag.parts_needed) {
      if (typeof diag.parts_needed === 'string') {
        try {
          partsList = JSON.parse(diag.parts_needed);
        } catch (_) {
          partsList = [];
        }
      } else if (Array.isArray(diag.parts_needed)) {
        partsList = diag.parts_needed;
      }
    }

    let photosList: string[] = [];
    if (diag.photos) {
      if (typeof diag.photos === 'string') {
        try {
          photosList = JSON.parse(diag.photos);
        } catch (_) {
          photosList = [];
        }
      } else if (Array.isArray(diag.photos)) {
        photosList = diag.photos;
      }
    }

    return (
      <Card 
        title={<Space><SearchOutlined style={{ color: '#722ed1' }} /><span style={{ color: '#722ed1', fontWeight: 'bold' }}>Constat & Diagnostic Initial (#{diag.id})</span></Space>}
        style={{ marginBottom: '16px', border: '1px solid #d3adf7', borderRadius: '8px' }}
        headStyle={{ background: '#f9f0ff' }}
      >
        <Descriptions bordered column={2} size="small" style={{ marginBottom: '16px' }}>
          <Descriptions.Item label="Date du constat">
            {diag.submitted_at ? dayjs(diag.submitted_at).format('DD/MM/YYYY HH:mm') : 'N/A'}
          </Descriptions.Item>
          <Descriptions.Item label="Niveau d'urgence">
            <Tag color={diag.urgency_level === 'urgent' || diag.urgency_level === 'critical' ? 'red' : diag.urgency_level === 'high' ? 'orange' : 'blue'}>
              {(diag.urgency_level || 'Normal').toUpperCase()}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Description du Problème" span={2}>
            <div style={{ whiteSpace: 'pre-wrap', color: '#262626', fontWeight: 500 }}>
              {diag.problem_description || 'Non renseigné'}
            </div>
          </Descriptions.Item>
          <Descriptions.Item label="Solution Recommandée" span={2}>
            <div style={{ whiteSpace: 'pre-wrap', color: '#096dd9' }}>
              {diag.recommended_solution || 'Non renseigné'}
            </div>
          </Descriptions.Item>
          {diag.materials_needed && (
            <Descriptions.Item label="Matériels nécessaires" span={2}>
              <div style={{ whiteSpace: 'pre-wrap' }}>{diag.materials_needed}</div>
            </Descriptions.Item>
          )}
          {diag.estimated_duration && (
            <Descriptions.Item label="Durée estimée">
              {diag.estimated_duration}
            </Descriptions.Item>
          )}
          {diag.labor_cost > 0 && (
            <Descriptions.Item label="Coût main d'œuvre estimé">
              {Number(diag.labor_cost).toLocaleString()} FCFA
            </Descriptions.Item>
          )}
        </Descriptions>

        {/* Équipements constatés au diagnostic */}
        {equipmentsList.length > 0 && (
          <div style={{ marginTop: '16px' }}>
            <Title level={5}>🔧 Équipements constatés au diagnostic ({equipmentsList.length})</Title>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {equipmentsList.map((eq: any, idx: number) => (
                <div key={idx} style={{ padding: '12px', background: '#f5f5f5', borderRadius: '8px', border: '1px solid #d9d9d9' }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#722ed1' }}>
                    #{eq.index || idx + 1} {eq.name || `${eq.brand || 'Équipement'} ${eq.type ? `- ${eq.type}` : ''}`}
                    {eq.state && <Tag color="default" style={{ marginLeft: '8px' }}>État: {eq.state}</Tag>}
                    {eq.location && <Tag color="blue" style={{ marginLeft: '8px' }}>Emplacement: {eq.location}</Tag>}
                    {eq.tested !== undefined && eq.tested !== null && (
                      <Tag color={eq.tested ? 'success' : 'error'} style={{ marginLeft: '8px' }}>
                        Test: {eq.tested ? 'Oui' : 'Non'}
                      </Tag>
                    )}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px', padding: '8px', background: '#fff', borderRadius: '6px' }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '11px', color: '#8c8c8c' }}>Intensité</div>
                      <div style={{ fontWeight: 'bold', color: '#722ed1' }}>{eq.before_intensite || eq.intensite || '-'} {eq.before_intensite || eq.intensite ? 'A' : ''}</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '11px', color: '#8c8c8c' }}>Tension</div>
                      <div style={{ fontWeight: 'bold', color: '#722ed1' }}>{eq.before_tension || eq.tension || '-'} {eq.before_tension || eq.tension ? 'V' : ''}</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '11px', color: '#8c8c8c' }}>Pression</div>
                      <div style={{ fontWeight: 'bold', color: '#722ed1' }}>{eq.before_pression || eq.pression || '-'} {eq.before_pression || eq.pression ? 'bar' : ''}</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '11px', color: '#8c8c8c' }}>Puissance</div>
                      <div style={{ fontWeight: 'bold', color: '#722ed1' }}>{eq.before_puissance || eq.puissance || '-'} {eq.before_puissance || eq.puissance ? 'CV' : ''}</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '11px', color: '#8c8c8c' }}>Type de Fréon</div>
                      <div style={{ fontWeight: 'bold', color: '#722ed1' }}>{eq.before_freon || eq.freon || '-'}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pièces nécessaires & Devis */}
        {(partsList.length > 0 || quoteItem) && (
          <div style={{ marginTop: '16px' }}>
            <Title level={5}>📦 Pièces recommandées & Devis</Title>
            <div style={{ padding: '12px', background: '#fffbe6', borderRadius: '8px', border: '1px solid #ffe58f' }}>
              {quoteItem && (
                <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: 'bold', color: '#d46b08' }}>
                    💰 Devis associé #{quoteItem.id} — Statut: <Tag color={quoteItem.status === 'accepted' || quoteItem.status === 'paid' ? 'green' : 'orange'}>{quoteItem.status}</Tag>
                  </span>
                  <span style={{ fontSize: '15px', fontWeight: 'bold', color: '#d46b08' }}>
                    Total: {(quoteItem.total_amount || quoteItem.total || 0).toLocaleString()} FCFA
                  </span>
                </div>
              )}
              {partsList.length > 0 && (
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  {partsList.map((p: any, idx: number) => (
                    <li key={idx} style={{ marginBottom: '4px' }}>
                      <strong>{p.name}</strong> — Qté: {p.quantity || 1} {p.unit_price ? `(${Number(p.unit_price).toLocaleString()} FCFA/u)` : ''}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {/* Photos du diagnostic initial */}
        {photosList.length > 0 && (
          <div style={{ marginTop: '16px' }}>
            <Title level={5}>📷 Photos du constat initial ({photosList.length})</Title>
            <div style={{ padding: '12px', background: '#f5f5f5', borderRadius: '8px' }}>
              <Image.PreviewGroup>
                <Space size={12} wrap>
                  {photosList.map((p: string, idx: number) => (
                    <Image
                      key={idx}
                      src={getImageUrl(p)}
                      width={120}
                      height={120}
                      style={{ objectFit: 'cover', borderRadius: '8px', border: '2px solid #722ed1' }}
                    />
                  ))}
                </Space>
              </Image.PreviewGroup>
            </div>
          </div>
        )}
      </Card>
    );
  };

  const renderInterventionReportContent = (reportData: any, reportImages: any[]) => {
    const isInitial = reportData.report.initial_completed || reportData.initial_completed || !!reportData.report.problem_description;
    return (
      <Card 
        title={
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
            <ToolOutlined />
            <span>Rapport Technique & Travaux</span>
            {isInitial && (
              <Tag color="orange" style={{ fontWeight: 'bold' }}>
                📋 Constat & Données Techniques (Points 1 à 7)
              </Tag>
            )}
          </div>
        } 
        style={{ marginBottom: '16px', borderRadius: '8px' }}
      >
        <Descriptions bordered column={2} size="small">
          <Descriptions.Item label="Date de soumission" span={2}>
            {dayjs(reportData.report.submitted_at).format('DD/MM/YYYY à HH:mm')}
          </Descriptions.Item>
        </Descriptions>

        {/* Section Équipements */}
        <div style={{ marginTop: '16px' }}>
          <Title level={5}>🔧 Équipements ({reportData.report.equipments?.length || 1})</Title>
          
          {/* Afficher le format multi-équipements si disponible */}
          {reportData.report.equipments && reportData.report.equipments.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {reportData.report.equipments.map((equipment: any, index: number) => (
                <div 
                  key={index}
                  style={{ 
                    padding: '16px',
                    backgroundColor: '#f5f5f5',
                    borderRadius: '8px',
                    border: '1px solid #d9d9d9'
                  }}
                >
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    marginBottom: '12px',
                    paddingBottom: '8px',
                    borderBottom: '1px solid #e8e8e8'
                  }}>
                    <div style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      backgroundColor: '#0a543d',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      marginRight: '10px'
                    }}>
                      {index + 1}
                    </div>
                    <span style={{ fontWeight: 'bold', fontSize: '15px' }}>
                      {equipment.brand && equipment.type 
                        ? `${equipment.brand} - ${equipment.type}`
                        : (equipment.name 
                          ? equipment.name 
                          : `Équipement ${index + 1} (${equipment.type || 'Mural'})`)}
                    </span>
                    {equipment.tested !== undefined && equipment.tested !== null && (
                      <Tag 
                        color={equipment.tested ? 'success' : 'error'} 
                        style={{ marginLeft: 'auto', fontWeight: 'bold', fontSize: '12px', padding: '2px 8px' }}
                      >
                        {equipment.tested ? '✅ 4/ Test : Oui' : '❌ 4/ Test : Non'}
                      </Tag>
                    )}
                  </div>
                  <Row gutter={[16, 8]} style={{ marginBottom: '10px' }}>
                    <Col xs={24} sm={12} md={6}>
                      <div style={{ fontSize: '12px', color: '#8c8c8c' }}>1/ Type & Marque</div>
                      <div style={{ fontWeight: 'bold', color: '#262626' }}>
                        {equipment.type || 'Mural'} {equipment.brand ? `• ${equipment.brand}` : ''}
                      </div>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                      <div style={{ fontSize: '12px', color: '#8c8c8c' }}>2/ Emplacement</div>
                      <div style={{ fontWeight: 'bold', color: '#262626' }}>
                        {equipment.location || 'Non renseigné'}
                      </div>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                      <div style={{ fontSize: '12px', color: '#8c8c8c' }}>3/ État de l'équipement</div>
                      <div style={{ fontWeight: 'bold', color: '#262626' }}>
                        {equipment.state || 'Non renseigné'}
                      </div>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                      <div style={{ fontSize: '12px', color: '#8c8c8c' }}>4/ Test équipement</div>
                      <div style={{ fontWeight: 'bold' }}>
                        {equipment.tested === true ? (
                          <Tag color="green">Oui (Conforme)</Tag>
                        ) : equipment.tested === false ? (
                          <Tag color="red">Non (Non conforme)</Tag>
                        ) : (
                          <span style={{ color: '#8c8c8c' }}>Non testé</span>
                        )}
                      </div>
                    </Col>
                  </Row>

                  {/* 5/ Données techniques (Constantes relevées avant intervention) */}
                  {(() => {
                    const bIntensite = equipment.before_intensite || equipment.intensite;
                    const bTension = equipment.before_tension || equipment.tension;
                    const bPression = equipment.before_pression || equipment.pression;
                    const bPuissance = equipment.before_puissance || equipment.puissance;
                    const bFreon = equipment.before_freon || equipment.freon;
                    const hasBefore = bIntensite || bTension || bPression || bPuissance || bFreon;

                    if (!hasBefore) return null;

                    return (
                      <div style={{ marginTop: '12px' }}>
                        <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#d46b08', marginBottom: '8px' }}>
                          🟠 5/ Données techniques (Constantes relevées AVANT intervention)
                        </div>
                        <div style={{ 
                          display: 'grid', 
                          gridTemplateColumns: 'repeat(5, 1fr)', 
                          gap: '12px',
                          padding: '12px',
                          backgroundColor: '#fff7e6',
                          borderRadius: '8px',
                          border: '1px solid #ffd591'
                        }}>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '11px', color: '#8c8c8c' }}>Intensité</div>
                            <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#d46b08' }}>
                              {bIntensite ? `${bIntensite} A` : '-'}
                            </div>
                          </div>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '11px', color: '#8c8c8c' }}>Tension</div>
                            <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#d46b08' }}>
                              {bTension ? `${bTension} V` : '-'}
                            </div>
                          </div>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '11px', color: '#8c8c8c' }}>Pression</div>
                            <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#d46b08' }}>
                              {bPression ? `${bPression} bar` : '-'}
                            </div>
                          </div>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '11px', color: '#8c8c8c' }}>Puissance</div>
                            <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#d46b08' }}>
                              {bPuissance ? `${bPuissance} CV` : '-'}
                            </div>
                          </div>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '11px', color: '#8c8c8c' }}>Type de Fréon</div>
                            <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#d46b08' }}>
                              {bFreon || '-'}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Mesures techniques APRÈS intervention */}
                  {(() => {
                    const aIntensite = equipment.after_intensite;
                    const aTension = equipment.after_tension;
                    const aPression = equipment.after_pression;
                    const aPuissance = equipment.after_puissance;
                    const aFreon = equipment.after_freon;
                    const hasAfter = aIntensite || aTension || aPression || aPuissance || aFreon;

                    if (!hasAfter) return null;

                    return (
                      <div style={{ marginTop: '12px' }}>
                        <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#0a543d', marginBottom: '8px' }}>
                          🟢 Données Techniques — APRÈS Intervention
                        </div>
                        <div style={{ 
                          display: 'grid', 
                          gridTemplateColumns: 'repeat(5, 1fr)', 
                          gap: '12px',
                          padding: '12px',
                          backgroundColor: '#f6ffed',
                          borderRadius: '8px',
                          border: '1px solid #b7eb8f'
                        }}>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '11px', color: '#8c8c8c' }}>Intensité</div>
                            <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#0a543d' }}>
                              {aIntensite ? `${aIntensite} A` : '-'}
                            </div>
                          </div>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '11px', color: '#8c8c8c' }}>Tension</div>
                            <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#0a543d' }}>
                              {aTension ? `${aTension} V` : '-'}
                            </div>
                          </div>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '11px', color: '#8c8c8c' }}>Pression</div>
                            <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#0a543d' }}>
                              {aPression ? `${aPression} bar` : '-'}
                            </div>
                          </div>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '11px', color: '#8c8c8c' }}>Puissance</div>
                            <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#0a543d' }}>
                              {aPuissance ? `${aPuissance} CV` : '-'}
                            </div>
                          </div>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '11px', color: '#8c8c8c' }}>Type de Fréon</div>
                            <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#0a543d' }}>
                              {aFreon || '-'}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              ))}
            </div>
          ) : (
            /* Format legacy - un seul équipement */
            <div style={{ 
              padding: '16px',
              backgroundColor: '#f5f5f5',
              borderRadius: '8px',
              border: '1px solid #d9d9d9'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                marginBottom: '12px',
                paddingBottom: '8px',
                borderBottom: '1px solid #e8e8e8'
              }}>
                <span style={{ fontWeight: 'bold', fontSize: '15px' }}>
                  {reportData.report.equipment_brand && reportData.report.equipment_type 
                    ? `${reportData.report.equipment_brand} - ${reportData.report.equipment_type}`
                    : (reportData.report.equipment_name || 'Équipement Principal')}
                </span>
                {reportData.report.tested !== undefined && reportData.report.tested !== null && (
                  <Tag 
                    color={reportData.report.tested ? 'success' : 'error'} 
                    style={{ marginLeft: 'auto', fontWeight: 'bold', fontSize: '12px', padding: '2px 8px' }}
                  >
                    {reportData.report.tested ? '✅ 4/ Test : Oui' : '❌ 4/ Test : Non'}
                  </Tag>
                )}
              </div>
              <Row gutter={[16, 8]} style={{ marginBottom: '10px' }}>
                <Col xs={24} sm={12} md={6}>
                  <div style={{ fontSize: '12px', color: '#8c8c8c' }}>1/ Type & Marque</div>
                  <div style={{ fontWeight: 'bold', color: '#262626' }}>
                    {reportData.report.equipment_type || 'Mural'} {reportData.report.equipment_brand ? `• ${reportData.report.equipment_brand}` : ''}
                  </div>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <div style={{ fontSize: '12px', color: '#8c8c8c' }}>2/ Emplacement</div>
                  <div style={{ fontWeight: 'bold', color: '#262626' }}>
                    {reportData.report.location || reportData.report.equipment_location || 'Non renseigné'}
                  </div>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <div style={{ fontSize: '12px', color: '#8c8c8c' }}>3/ État de l'équipement</div>
                  <div style={{ fontWeight: 'bold', color: '#262626' }}>
                    {reportData.report.equipment_state || 'Non renseigné'}
                  </div>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <div style={{ fontSize: '12px', color: '#8c8c8c' }}>4/ Test équipement</div>
                  <div style={{ fontWeight: 'bold' }}>
                    {reportData.report.tested === true ? (
                      <Tag color="green">Oui (Conforme)</Tag>
                    ) : reportData.report.tested === false ? (
                      <Tag color="red">Non (Non conforme)</Tag>
                    ) : (
                      <span style={{ color: '#8c8c8c' }}>Non testé</span>
                    )}
                  </div>
                </Col>
              </Row>
              {/* 5/ Données techniques legacy */}
              {(reportData.report.pression || reportData.report.puissance || reportData.report.temperature ||
                reportData.report.intensite || reportData.report.tension || reportData.report.freon) && (
                <div style={{ marginTop: '12px' }}>
                  <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#d46b08', marginBottom: '8px' }}>
                    🟠 5/ Données techniques (Constantes relevées AVANT intervention)
                  </div>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(5, 1fr)', 
                    gap: '12px',
                    padding: '12px',
                    backgroundColor: '#fff7e6',
                    borderRadius: '8px',
                    border: '1px solid #ffd591'
                  }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '11px', color: '#8c8c8c' }}>Intensité</div>
                      <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#d46b08' }}>
                        {reportData.report.intensite ? `${reportData.report.intensite} A` : '-'}
                      </div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '11px', color: '#8c8c8c' }}>Tension</div>
                      <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#d46b08' }}>
                        {reportData.report.tension ? `${reportData.report.tension} V` : '-'}
                      </div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '11px', color: '#8c8c8c' }}>Pression</div>
                      <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#d46b08' }}>
                        {reportData.report.pression ? `${reportData.report.pression} bar` : '-'}
                      </div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '11px', color: '#8c8c8c' }}>Puissance</div>
                      <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#d46b08' }}>
                        {(reportData.report.puissance || reportData.report.temperature) ? `${reportData.report.puissance || reportData.report.temperature} CV` : '-'}
                      </div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '11px', color: '#8c8c8c' }}>Type de Fréon</div>
                      <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#d46b08' }}>
                        {reportData.report.freon || '-'}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Section Travaux Effectués */}
        {(() => {
          if (!reportData.report.tasks_done) return null;
          const taskItems = [
            // Maintenance
            { key: 'filtres_air', label: 'Nettoyage des filtres à air' },
            { key: 'batterie_evaporateur', label: 'Nettoyage de la batterie évaporateur' },
            { key: 'bacs_condensat', label: 'Nettoyage des bacs à condensat' },
            { key: 'turbine', label: 'Nettoyage de la turbine' },
            { key: 'condenseur', label: 'Nettoyage du condenseur' },
            { key: 'carrosserie_evaporateur', label: 'Nettoyage de la carrosserie évaporateur' },
            { key: 'tuyauterie_evacuation', label: 'Soufflement de la tuyauterie d\'évacuation' },
            { key: 'parties_electriques', label: 'Nettoyage des parties électriques' },
            { key: 'volets_air', label: 'Nettoyage des volets d\'air' },
            // Dépannage
            { key: 'diagnostic_panne', label: 'Diagnostic et identification de la panne' },
            { key: 'remplacement_piece', label: 'Remplacement de composant(s) défectueux' },
            { key: 'recherche_fuite', label: 'Recherche et colmatage de fuite frigorifique' },
            { key: 'recharge_freon', label: 'Recharge / appoint en fluide frigorigène (Fréon)' },
            { key: 'evacuation_condensat', label: 'Débouchage / nettoyage évacuation des condensats' },
            { key: 'circuit_electrique', label: 'Réparation / réfection du circuit électrique' },
            { key: 'nettoyage_filtres', label: 'Nettoyage des filtres et serpentins' },
            { key: 'test_fonctionnement', label: 'Contrôle des paramètres et test de bon fonctionnement' },
            // Installation (7 incontournables officiels)
            { key: 'installation_equerre_evaporateur', label: 'Installation en équerre de l’évaporateur' },
            { key: 'installation_unite_exterieure', label: 'Installation de l’unité extérieure' },
            { key: 'remplacement_tuyauterie', label: 'Remplacement de la tuyauterie' },
            { key: 'installation_support_metallique', label: 'Installation du support métallique' },
            { key: 'cablage_electrique', label: 'Câblage électrique' },
            { key: 'tirage_au_vide', label: 'Tirage au vide' },
            { key: 'mise_en_route', label: 'Mise en route' },
            // Installation (clés legacy)
            { key: 'pose_unite_interieure', label: 'Fixation et pose de l\'unité intérieure' },
            { key: 'pose_groupe_exterieur', label: 'Fixation et pose du groupe extérieur' },
            { key: 'liaisons_frigo', label: 'Pose et raccordement des liaisons frigorifiques' },
            { key: 'raccordement_electrique', label: 'Raccordement électrique et protection (disjoncteur)' },
            { key: 'test_etancheite', label: 'Test d\'étanchéité sous pression' },
            { key: 'mise_en_service', label: 'Mise en service et contrôle des pressions / températures' },
          ];
          const doneItems = taskItems.filter(
            (item) => reportData.report.tasks_done?.[item.key] === true || reportData.report.tasks_done?.[item.key] === 1
          );
          if (doneItems.length === 0) return null;

          return (
            <div style={{ marginTop: '16px' }}>
              <Title level={5}>🛠️ Travaux Effectués</Title>
              <div style={{ 
                padding: '16px',
                backgroundColor: '#f6ffed',
                borderRadius: '8px',
                border: '1px solid #b7eb8f'
              }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
                  {doneItems.map((item) => (
                    <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '16px' }} />
                      <span style={{ fontWeight: '500', fontSize: '13px' }}>{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })()}

        {/* Section Détail de l'Intervention */}
        <div style={{ marginTop: '16px' }}>
          <Title level={5}>📋 Détail de l'Intervention</Title>
          <div style={{ 
            padding: '16px',
            backgroundColor: '#e6f7ff',
            borderRadius: '8px',
            border: '1px solid #91d5ff'
          }}>
            <Row gutter={[16, 8]}>
              {reportData.report.technician_name && (
                <Col span={12}>
                  <div style={{ fontSize: '12px', color: '#8c8c8c' }}>Intervenant</div>
                  <div style={{ fontWeight: 'bold' }}>{reportData.report.technician_name}</div>
                </Col>
              )}
              {reportData.report.intervention_date && (
                <Col span={12}>
                  <div style={{ fontSize: '12px', color: '#8c8c8c' }}>Date</div>
                  <div style={{ fontWeight: 'bold' }}>
                    {dayjs(reportData.report.intervention_date).format('DD/MM/YYYY')}
                  </div>
                </Col>
              )}
              {reportData.report.start_time && (
                <Col span={6}>
                  <div style={{ fontSize: '12px', color: '#8c8c8c' }}>Début</div>
                  <div style={{ fontWeight: 'bold' }}>{reportData.report.start_time}</div>
                </Col>
              )}
              {reportData.report.end_time && (
                <Col span={6}>
                  <div style={{ fontSize: '12px', color: '#8c8c8c' }}>Fin</div>
                  <div style={{ fontWeight: 'bold' }}>{reportData.report.end_time}</div>
                </Col>
              )}
              {reportData.report.duration > 0 && (
                <Col span={12}>
                  <div style={{ fontSize: '12px', color: '#8c8c8c' }}>Durée</div>
                  <div style={{ fontWeight: 'bold' }}>{reportData.report.duration} minutes</div>
                </Col>
              )}
            </Row>
            
            {/* Nature de l'intervention */}
            <div style={{ marginTop: '12px' }}>
              <div style={{ fontSize: '13px', fontWeight: '500', color: '#0a543d', marginBottom: '8px' }}>
                Nature de l'intervention
              </div>
              <div style={{ 
                whiteSpace: 'pre-wrap', 
                padding: '12px', 
                backgroundColor: '#fff', 
                borderRadius: '4px',
                border: '1px solid #d9d9d9'
              }}>
                {reportData.report.intervention_nature || 'Non spécifié'}
              </div>
            </div>

            {/* 6/ Décrire la panne (constat du technicien) */}
            {(reportData.report.problem_description || reportData.problem_description || (selectedDiag && selectedDiag.problem_description)) && (
              <div style={{ marginTop: '12px' }}>
                <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#cf1322', marginBottom: '8px' }}>
                  ⚠️ 6/ Décrire la panne (Constat du technicien)
                </div>
                <div style={{ 
                  whiteSpace: 'pre-wrap', 
                  padding: '12px', 
                  backgroundColor: '#fff1f0', 
                  borderRadius: '6px',
                  border: '1px solid #ffa39e',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  color: '#cf1322'
                }}>
                  {reportData.report.problem_description || reportData.problem_description || (selectedDiag && selectedDiag.problem_description)}
                </div>
              </div>
            )}

            {/* 7/ Matériels nécessaires pour le dépannage / l'installation */}
            {(reportData.report.materials_needed || reportData.materials_needed || (selectedDiag && selectedDiag.materials_needed)) && (
              <div style={{ marginTop: '12px' }}>
                <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#096dd9', marginBottom: '8px' }}>
                  🧰 7/ Matériels nécessaires pour le dépannage / l'installation
                </div>
                <div style={{ 
                  whiteSpace: 'pre-wrap', 
                  padding: '12px', 
                  backgroundColor: '#e6f7ff', 
                  borderRadius: '6px',
                  border: '1px solid #91d5ff',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  color: '#003a8c'
                }}>
                  {reportData.report.materials_needed || reportData.materials_needed || (selectedDiag && selectedDiag.materials_needed)}
                </div>
              </div>
            )}

            {/* Description des travaux réalisés / dépannage */}
            {reportData.report.work_description && (
              <div style={{ marginTop: '12px' }}>
                <div style={{ fontSize: '13px', fontWeight: '500', color: '#0a543d', marginBottom: '8px' }}>
                  📝 Description des travaux réalisés / Dépannage
                </div>
                <div style={{ 
                  whiteSpace: 'pre-wrap', 
                  padding: '12px', 
                  backgroundColor: '#fff', 
                  borderRadius: '4px',
                  border: '1px solid #d9d9d9',
                  fontSize: '14px',
                  lineHeight: '1.5'
                }}>
                  {reportData.report.work_description}
                </div>
              </div>
            )}

            {/* Conclusion de l'installation (Typologie) */}
            {reportData.report.conclusion_installation && (
              <div style={{ marginTop: '12px' }}>
                <div style={{ fontSize: '13px', fontWeight: '500', color: '#0a543d', marginBottom: '8px' }}>
                  🎯 Conclusion de l'installation (Typologie de pose)
                </div>
                <div style={{ 
                  whiteSpace: 'pre-wrap', 
                  padding: '12px', 
                  backgroundColor: '#f6ffed', 
                  borderRadius: '4px',
                  border: '1px solid #b7eb8f'
                }}>
                  {reportData.report.conclusion_installation}
                </div>
              </div>
            )}
            
            {/* Observations */}
            {reportData.report.observations && (
              <div style={{ marginTop: '12px' }}>
                <div style={{ fontSize: '13px', fontWeight: '500', color: '#cf7a00', marginBottom: '8px' }}>
                  Observations
                </div>
                <div style={{ 
                  whiteSpace: 'pre-wrap', 
                  padding: '12px', 
                  backgroundColor: '#fff7e6', 
                  borderRadius: '4px',
                  border: '1px solid #ffd591' 
                }}>
                  {reportData.report.observations}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Section Pièces de rechange */}
        {(reportData.report.spare_parts && reportData.report.spare_parts.length > 0) || 
         (reportData.report.materials_used && reportData.report.materials_used.length > 0) ? (
          <div style={{ marginTop: '16px' }}>
            <Title level={5}>📦 Produit et Pièces de Rechange</Title>
            <div style={{ 
              padding: '16px',
              backgroundColor: '#f6ffed',
              borderRadius: '8px',
              border: '1px solid #b7eb8f'
            }}>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                {(reportData.report.spare_parts || reportData.report.materials_used || []).map((part: any, index: number) => (
                  <li key={index} style={{ marginBottom: '8px' }}>
                    <strong>{part.name}</strong>
                    {part.quantity && <span style={{ marginLeft: '8px', color: '#52c41a', fontWeight: 'bold' }}>
                      x{part.quantity}
                    </span>}
                    {part.unit && ` ${part.unit}`}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : null}

        {/* Photos AVANT et APRÈS depuis report_data */}
        {((reportData.report.photos_before && reportData.report.photos_before.length > 0) ||
          (reportData.report.photos_after && reportData.report.photos_after.length > 0)) && (
          <div style={{ marginTop: '16px' }}>
            <Title level={5}>📷 Photos du rapport{reportData.report.photos_before && reportData.report.photos_before.length > 0 ? ' (Avant / Après)' : ''}</Title>
            <div style={{ padding: '16px', background: '#f5f5f5', borderRadius: '8px' }}>
              {reportData.report.photos_before && reportData.report.photos_before.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#d46b08', marginBottom: '8px' }}>
                    📸 Photos AVANT intervention ({reportData.report.photos_before.length})
                  </div>
                  <Image.PreviewGroup>
                    <Space size={12} wrap>
                      {reportData.report.photos_before.map((path: string, idx: number) => (
                        <Image
                          key={idx}
                          src={getImageUrl(path)}
                          width={120}
                          height={120}
                          style={{ objectFit: 'cover', borderRadius: '8px', border: '2px solid #fa8c16' }}
                        />
                      ))}
                    </Space>
                  </Image.PreviewGroup>
                </div>
              )}

              {reportData.report.photos_after && reportData.report.photos_after.length > 0 && (
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#0a543d', marginBottom: '8px' }}>
                    📸 {reportData.report.photos_before && reportData.report.photos_before.length > 0 ? 'Photos APRÈS intervention' : 'Photos de l\'Intervention'} ({reportData.report.photos_after.length})
                  </div>
                  <Image.PreviewGroup>
                    <Space size={12} wrap>
                      {reportData.report.photos_after.map((path: string, idx: number) => {
                        const isVideo = path.match(/\.(mp4|mov|avi)$/i);
                        return isVideo ? (
                          <video
                            key={idx}
                            src={getImageUrl(path)}
                            controls
                            style={{ width: 200, height: 120, objectFit: 'contain', borderRadius: '8px', border: '2px solid #52c41a', backgroundColor: '#000' }}
                          />
                        ) : (
                          <Image
                            key={idx}
                            src={getImageUrl(path)}
                            width={120}
                            height={120}
                            style={{ objectFit: 'cover', borderRadius: '8px', border: '2px solid #52c41a' }}
                          />
                        );
                      })}
                    </Space>
                  </Image.PreviewGroup>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Photos - Afficher les images (uniquement si aucune photo Avant/Après n'est disponible) */}
        {reportImages.length > 0 &&
          (!reportData.report.photos_before || reportData.report.photos_before.length === 0) &&
          (!reportData.report.photos_after || reportData.report.photos_after.length === 0) && (
          <div style={{ marginTop: '16px' }}>
            <Title level={5}>📷 Photos de l'intervention ({reportImages.length})</Title>
            <div style={{ 
              padding: '16px', 
              background: '#f5f5f5', 
              borderRadius: '8px'
            }}>
              <Image.PreviewGroup>
                <Space size={12} wrap>
                  {reportImages
                    .sort((a: any, b: any) => a.order - b.order)
                    .map((image: any) => {
                      const isVideo = image.image_url.match(/\.(mp4|mov|avi)$/i);
                      return isVideo ? (
                        <video
                          key={image.id}
                          src={getImageUrl(image.image_url)}
                          controls
                          style={{
                            width: 200,
                            height: 120,
                            objectFit: 'contain',
                            borderRadius: '8px',
                            border: '2px solid #d9d9d9',
                            backgroundColor: '#000'
                          }}
                        />
                      ) : (
                        <Image
                          key={image.id}
                          src={getImageUrl(image.image_url)}
                          alt={`Photo ${image.order + 1}`}
                          width={120}
                          height={120}
                          style={{ 
                            objectFit: 'cover', 
                            borderRadius: '8px', 
                            cursor: 'pointer',
                            border: '2px solid #d9d9d9'
                          }}
                          placeholder={
                            <div style={{
                              width: 120,
                              height: 120,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: '#f0f0f0'
                            }}>
                              Chargement...
                            </div>
                          }
                        />
                      );
                    })}
                </Space>
              </Image.PreviewGroup>
            </div>
          </div>
        )}
      </Card>
    );
  };

  const renderMergedWorkflowSection = (reportData: any, diag: any, quoteItem: any, reportImages: any[]) => {
    return (
      <div>
        {/* Banner de Progression Chronologique */}
        <Card 
          size="small" 
          style={{ 
            marginBottom: '20px', 
            background: 'linear-gradient(135deg, #f0f5ff 0%, #f6ffed 100%)',
            border: '1px solid #adc6ff',
            borderRadius: '8px'
          }}
        >
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} md={8}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ 
                  width: '36px', height: '36px', borderRadius: '50%', 
                  backgroundColor: '#722ed1', color: 'white', 
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' 
                }}>
                  1
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#595959' }}>Étape 1 : Constat Initial</div>
                  <div style={{ fontWeight: 'bold', color: '#722ed1' }}>
                    Diagnostic #{diag.id}
                  </div>
                  <div style={{ fontSize: '11px', color: '#8c8c8c' }}>
                    {diag.submitted_at ? dayjs(diag.submitted_at).format('DD/MM/YYYY HH:mm') : 'Soumis'}
                  </div>
                </div>
              </div>
            </Col>
            <Col xs={24} md={8}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ 
                  width: '36px', height: '36px', borderRadius: '50%', 
                  backgroundColor: quoteItem ? '#fa8c16' : '#bfbfbf', color: 'white', 
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' 
                }}>
                  2
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#595959' }}>Étape 2 : Devis & Accord</div>
                  <div style={{ fontWeight: 'bold', color: quoteItem ? '#fa8c16' : '#595959' }}>
                    {quoteItem ? `Devis #${quoteItem.id} (${(quoteItem.total_amount || quoteItem.total || 0).toLocaleString()} FCFA)` : 'Devis direct'}
                  </div>
                  <div style={{ fontSize: '11px', color: '#8c8c8c' }}>
                    {quoteItem?.status ? `Statut : ${quoteItem.status}` : 'Devis validé'}
                  </div>
                </div>
              </div>
            </Col>
            <Col xs={24} md={8}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ 
                  width: '36px', height: '36px', borderRadius: '50%', 
                  backgroundColor: '#52c41a', color: 'white', 
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' 
                }}>
                  3
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#595959' }}>Étape 3 : Clôture Travaux</div>
                  <div style={{ fontWeight: 'bold', color: '#52c41a' }}>
                    Rapport Final Soumis
                  </div>
                  <div style={{ fontSize: '11px', color: '#8c8c8c' }}>
                    {reportData.report.submitted_at ? dayjs(reportData.report.submitted_at).format('DD/MM/YYYY HH:mm') : ''}
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Card>

        {/* Bloc 1 : Constat & Diagnostic Initial */}
        {renderDiagnosticSection(diag, quoteItem)}

        {/* Bloc 2 : Travaux Réalisés */}
        {renderInterventionReportContent(reportData, reportImages)}
      </div>
    );
  };

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>
        <FileTextOutlined /> Rapports d'Interventions
      </Title>

      {/* Statistiques */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Total Rapports"
              value={stats.total}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Soumis"
              value={stats.soumis}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Approuvés"
              value={stats.approuves}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filtres */}
      <Card style={{ marginBottom: '24px' }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Search
              placeholder="Rechercher (intervention, technicien, client...)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} md={6}>
            <Select
              placeholder="Statut"
              value={statusFilter}
              onChange={setStatusFilter}
              allowClear
              style={{ width: '100%' }}
            >
              <Select.Option value="">Tous</Select.Option>
              <Select.Option value="submitted">Soumis</Select.Option>
              <Select.Option value="approved">Approuvé</Select.Option>
              <Select.Option value="rejected">Rejeté</Select.Option>
            </Select>
          </Col>
        </Row>
      </Card>

      {/* Tableau */}
      <Card>
        <Table
          columns={columns}
          dataSource={filteredReports}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total} rapports`,
          }}
          onChange={handleTableChange}
          locale={{
            emptyText: 'Aucun rapport trouvé',
          }}
        />
      </Card>

      {/* Modal de détails */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FileTextOutlined />
            <span>Détails du Rapport d'Intervention #{selectedReport?.id}</span>
          </div>
        }
        open={detailModalVisible}
        onCancel={handleCloseModal}
        width={950}
        footer={[
          <Button key="close" onClick={handleCloseModal}>
            Fermer
          </Button>,
        ]}
      >
        {selectedReport && (
          <div>
            {/* Header Badges */}
            <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <Tag
                color={getStatusColor(selectedReport.report.status)}
                style={{ fontSize: '14px', padding: '4px 12px' }}
              >
                {getStatusLabel(selectedReport.report.status)}
              </Tag>
              {selectedReport.report.intervention_nature && (
                <Tag color="cyan" style={{ fontSize: '13px', padding: '3px 10px' }}>
                  {selectedReport.report.intervention_nature}
                </Tag>
              )}
              {selectedDiag && (
                <Tag color="purple" style={{ fontSize: '13px', padding: '3px 10px' }}>
                  🔍 Diagnostic Initial #{selectedDiag.id} Fusionné
                </Tag>
              )}
              {selectedQuote && (
                <Tag color="gold" style={{ fontSize: '13px', padding: '3px 10px' }}>
                  {isAgent 
                    ? `💰 Devis #${selectedQuote.id}` 
                    : `💰 Devis #${selectedQuote.id} (${(selectedQuote.total_amount || selectedQuote.total || 0).toLocaleString()} FCFA)`}
                </Tag>
              )}
            </div>

            {/* Informations Générales Intervention */}
            <Descriptions title="Intervention" bordered column={2} size="small" style={{ marginBottom: '16px' }}>
              <Descriptions.Item label="Titre" span={2}>
                {selectedReport.intervention_title}
              </Descriptions.Item>
              <Descriptions.Item label="Description" span={2}>
                {selectedReport.intervention_description || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Adresse" span={2}>
                {selectedReport.intervention_address || 'Non spécifiée'}
              </Descriptions.Item>
              <Descriptions.Item label="Date programmée">
                {dayjs(selectedReport.scheduled_date).format('DD/MM/YYYY HH:mm')}
              </Descriptions.Item>
              <Descriptions.Item label="Date de complétion">
                {(selectedReport.completed_at || selectedReport.completed_date)
                  ? dayjs(selectedReport.completed_at || selectedReport.completed_date).format('DD/MM/YYYY HH:mm')
                  : 'N/A'}
              </Descriptions.Item>
            </Descriptions>

            {/* Informations Client & Technicien */}
            <Row gutter={16} style={{ marginBottom: '20px' }}>
              <Col span={12}>
                <Descriptions title="Client" bordered column={1} size="small">
                  <Descriptions.Item label="Nom">
                    {selectedReport.customer?.name || 'N/A'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Email">
                    {selectedReport.customer?.email || 'N/A'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Téléphone">
                    {selectedReport.customer?.phone || 'N/A'}
                  </Descriptions.Item>
                </Descriptions>
              </Col>
              <Col span={12}>
                <Descriptions title="Technicien" bordered column={1} size="small">
                  <Descriptions.Item label="Nom">
                    {selectedReport.technician?.name || 'N/A'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Email">
                    {selectedReport.technician?.email || 'N/A'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Téléphone">
                    {selectedReport.technician?.phone || 'N/A'}
                  </Descriptions.Item>
                </Descriptions>
              </Col>
            </Row>

            {/* Onglets de contenu si diagnostic lié ou vue standard */}
            {selectedDiag ? (
              <Tabs 
                activeKey={activeTabKey} 
                onChange={setActiveTabKey}
                type="card"
                items={[
                  {
                    key: 'merged',
                    label: <span><AppstoreOutlined /> 📋 Synthèse Fusionnée (Diagnostic ➔ Clôture)</span>,
                    children: renderMergedWorkflowSection(selectedReport, selectedDiag, selectedQuote, interventionImages),
                  },
                  {
                    key: 'intervention',
                    label: <span><ToolOutlined /> 🛠️ Travaux Réalisés (Rapport Final)</span>,
                    children: renderInterventionReportContent(selectedReport, interventionImages),
                  },
                  {
                    key: 'diagnostic',
                    label: <span><SearchOutlined /> 🔍 Constat & Diagnostic Initial</span>,
                    children: renderDiagnosticSection(selectedDiag, selectedQuote),
                  },
                ]}
              />
            ) : (
              renderInterventionReportContent(selectedReport, interventionImages)
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default InterventionReportsPage;
